import React from "react";
import styled from "styled-components";

import FilterChip from "../../../../components/filter-chip/FilterChip";
import HelpHint from "../../../../components/help-hint/HelpHint";

/** チップを折り返しで並べる 1 行分のコンテナ */
const ChipRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/** ヘルプアイコンを右端に押し出すためのスペーサー */
const Spacer = styled.div`
  flex: 1;
  min-width: 8px;
`;

const HELP_DESCRIPTION = `Excel / スプレッドシートをコピーして貼り付けると、1 列目をタスク名、2 列目以降を各列として行・列ごと取り込めます（先頭行が「タスク」なら列ラベルになります）。
列ラベルはドラッグで並び替えできます。境界線が太い列は直列（左の完了を待つ）です。
担当者の後ろに「@山田[3h]」と書くと作業時間として集計され、アクティビティ集計ページで月ごとに確認できます（[1.5h] / [30m] も可）。`;

interface MentionFilterBarProps {
  /** ボード本文に登場した `@表示名`（出現順・重複排除） */
  mentions: string[];
  /** 選択中の担当者名（複数 = OR）。空 = すべて */
  selected: string[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (name: string) => void;
  onClear: () => void;
  /** 右端ヘルプアイコンを表示するか（編集権限あり時のみ true 推奨） */
  showHelp?: boolean;
  /**
   * 担当者チップの色（アクティビティ集計と同じユーザー配色）。
   * undefined を返した名前は既定の青のまま。
   */
  // eslint-disable-next-line no-unused-vars
  colorOf?: (name: string) => string | undefined;
}

/**
 * タスクボード（タスク / スケジュール / 工数の全ビュー共通）の担当者フィルター。
 * **タスク表の本文**に書かれた `@表示名` をタグとして並べ、クリックでその担当の
 * タスクのみに絞り込む（複数選択 = OR）。
 * 先頭の「すべて」はフィルター解除（＝全件表示）を兼ねる。
 * 右端のヘルプアイコンは、貼り付け／並び替え／直列列ボーダーの使い方を吹き出しで案内する。
 *
 * `colorOf` を渡すと、担当者チップをアクティビティ集計と同じユーザー配色で
 * 塗り分ける（実績のない担当者は既定の青のまま）。
 *
 * NOTE: 以前はメンバー表示名と完全一致した場合にアバターを併記していたが、
 * テキストとの併記は情報過多のため一旦非表示にした（将来どちらかに統一予定）。
 */
const MentionFilterBar: React.FC<MentionFilterBarProps> = ({
  mentions,
  selected,
  onToggle,
  onClear,
  showHelp = false,
  colorOf,
}) => {
  return (
    <ChipRow>
      <FilterChip
        selected={selected.length === 0}
        onClick={onClear}
        className="u-flex-shrink-0"
      >
        すべて
      </FilterChip>
      {mentions.map((name) => (
        <FilterChip
          key={name}
          selected={selected.includes(name)}
          color={colorOf?.(name)}
          onClick={() => onToggle(name)}
        >
          {`@${name}`}
        </FilterChip>
      ))}
      {showHelp && (
        <>
          <Spacer />
          <HelpHint description={HELP_DESCRIPTION} ariaLabel="使い方のヘルプ" />
        </>
      )}
    </ChipRow>
  );
};

export default MentionFilterBar;
